import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { SlidersHorizontal, Plus, Trash2, X } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { CUSTOM_ENTITIES, FIELD_TYPES } from "@/lib/constants";
import api from "@/lib/apiClient";

export default function CustomFields() {
  const [entity, setEntity] = useState("claim");
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ label: "", field_type: "text", required: false, options: [] });
  const [optInput, setOptInput] = useState("");

  const load = useCallback(async (ent) => {
    setLoading(true);
    try { setFields((await api.get("/custom-fields", { params: { entity: ent } })).data); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(entity); }, [entity, load]);

  const addOption = () => {
    if (!optInput.trim()) return;
    setForm((f) => ({ ...f, options: [...f.options, optInput.trim()] }));
    setOptInput("");
  };

  const create = async () => {
    if (!form.label.trim()) { toast.error("Field label is required."); return; }
    if (form.field_type === "dropdown" && form.options.length === 0) { toast.error("Add at least one dropdown option."); return; }
    try {
      await api.post("/custom-fields", { entity, ...form });
      toast.success("Custom field added.");
      setForm({ label: "", field_type: "text", required: false, options: [] });
      load(entity);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed."); }
  };

  const remove = async (id) => {
    try { await api.delete(`/custom-fields/${id}`); toast.success("Field removed."); load(entity); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed."); }
  };

  return (
    <div className="space-y-6" data-testid="custom-fields-page">
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-lg bg-primary/10 flex items-center justify-center">
          <SlidersHorizontal className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h1 className="font-display text-3xl font-extrabold">Custom Fields</h1>
          <p className="text-muted-foreground">Add unlimited custom fields to any tab. They appear automatically in that tab's forms.</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {CUSTOM_ENTITIES.map((e) => (
          <button
            key={e.key}
            onClick={() => setEntity(e.key)}
            data-testid={`entity-tab-${e.key}`}
            className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${entity === e.key ? "bg-primary text-primary-foreground" : "bg-secondary text-secondary-foreground hover:bg-accent"}`}
          >
            {e.label}
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="p-6">
          <h3 className="font-display font-bold mb-4">Add Field to "{CUSTOM_ENTITIES.find((e) => e.key === entity)?.label}"</h3>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Field Label *</Label>
              <Input value={form.label} onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))} placeholder="e.g. Vehicle Number" data-testid="cf-label-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Field Type</Label>
              <Select value={form.field_type} onValueChange={(v) => setForm((f) => ({ ...f, field_type: v }))}>
                <SelectTrigger data-testid="cf-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{FIELD_TYPES.map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {form.field_type === "dropdown" && (
              <div className="space-y-1.5">
                <Label>Dropdown Options</Label>
                <div className="flex gap-2">
                  <Input value={optInput} onChange={(e) => setOptInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addOption())} placeholder="Type and press Enter" data-testid="cf-option-input" />
                  <Button type="button" variant="secondary" onClick={addOption}>Add</Button>
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {form.options.map((o, i) => (
                    <span key={i} className="inline-flex items-center gap-1.5 rounded-full bg-secondary px-3 py-1 text-xs">
                      {o}<button type="button" onClick={() => setForm((f) => ({ ...f, options: f.options.filter((_, j) => j !== i) }))}><X className="h-3 w-3" /></button>
                    </span>
                  ))}
                </div>
              </div>
            )}
            <div className="flex items-center justify-between rounded-lg border p-3">
              <Label>Required field</Label>
              <Switch checked={form.required} onCheckedChange={(v) => setForm((f) => ({ ...f, required: v }))} data-testid="cf-required-switch" />
            </div>
            <Button onClick={create} className="gap-2 w-full" data-testid="cf-add-button"><Plus className="h-4 w-4" /> Add Field</Button>
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="font-display font-bold mb-4">Existing Fields ({fields.length})</h3>
          {loading ? <p className="text-muted-foreground text-sm">Loading...</p> : fields.length === 0 ? (
            <p className="text-muted-foreground text-sm">No custom fields for this tab yet.</p>
          ) : (
            <div className="space-y-2">
              {fields.map((f) => (
                <div key={f.id} className="flex items-center justify-between rounded-lg border p-3" data-testid={`cf-row-${f.field_key}`}>
                  <div>
                    <p className="font-medium text-sm">{f.label} {f.required && <span className="text-red-500">*</span>}</p>
                    <p className="text-xs text-muted-foreground capitalize">{f.field_type}{f.field_type === "dropdown" && ` · ${f.options.join(", ")}`}</p>
                  </div>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="ghost" size="icon" className="text-red-600 hover:text-red-700" data-testid={`cf-delete-${f.field_key}`}><Trash2 className="h-4 w-4" /></Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader><AlertDialogTitle>Delete "{f.label}"?</AlertDialogTitle>
                        <AlertDialogDescription>This field will no longer appear in forms. Existing saved data is kept.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => remove(f.id)} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
